import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Clock } from 'lucide-react';
import { listAgentsAdmin } from '../../api/agentsAdmin';
import { getApiErrorMessage } from '../../utils/errors';
import { agentGraphUrl } from '../../utils/agentGraph';

const PER_PAGE = 20;

const STATUS_LABELS = {
  active: 'Ativo',
  inactive: 'Inativo',
};

function formatDate(iso) {
  if (!iso) return null;
  return new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short' }).format(new Date(iso));
}

function formatLlmSummary(profile) {
  const model = profile?.model_line?.label;
  const effort = profile?.effort?.label;
  const parts = [model, effort].filter(Boolean);
  return parts.length > 0 ? parts.join(' · ') : null;
}

function buildMetaLine(agent) {
  const parts = [];

  if (agent.account?.name) {
    parts.push(agent.account.name);
  }
  if (agent.account?.email) {
    parts.push(agent.account.email);
  }

  const phone = agent.channel?.phone_number;
  if (phone) {
    parts.push(phone);
  }

  const count = agent.sub_agents_count ?? 0;
  parts.push(`${count} comp.`);

  const llm = formatLlmSummary(agent.llm_profile);
  if (llm) {
    parts.push(llm);
  }

  const date = formatDate(agent.created_at);
  if (date) {
    parts.push(date);
  }

  return parts.join(' · ');
}

function StatusDot({ status }) {
  const isActive = status === 'active';

  return (
    <span
      className={`inline-flex items-center gap-1.5 text-xs ${
        isActive ? 'text-emerald-700' : 'text-content-subtle'
      }`}
      title={STATUS_LABELS[status] || status}
    >
      <span
        className={`h-1.5 w-1.5 rounded-full shrink-0 ${
          isActive ? 'bg-emerald-500' : 'bg-slate-300'
        }`}
      />
      {STATUS_LABELS[status] || status}
    </span>
  );
}

function AgentRow({ agent }) {
  const meta = buildMetaLine(agent);

  return (
    <li className="surface-card px-4 py-3.5 flex items-center justify-between gap-4 hover:bg-slate-50/60 transition-colors">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <p className="font-medium text-content-primary truncate">{agent.name}</p>
          <StatusDot status={agent.status} />
        </div>
        {meta && (
          <p className="text-xs text-content-muted mt-1 truncate" title={meta}>
            {meta}
          </p>
        )}
      </div>
      <div className="shrink-0 flex items-center gap-1.5">
        <Link
          to={`/admin/scheduled-messages?agentId=${agent.id}`}
          title="Mensagens agendadas"
          aria-label="Mensagens agendadas"
          className="inline-flex items-center justify-center h-7 w-7 rounded-md text-content-secondary border border-slate-200 bg-white hover:bg-slate-50 hover:text-content-primary transition-colors"
        >
          <Clock className="h-3.5 w-3.5 shrink-0" aria-hidden />
        </Link>
        <a
          href={agentGraphUrl(agent.id)}
          target="_blank"
          rel="noopener noreferrer"
          title="Abrir pipeline"
          aria-label="Abrir pipeline"
          className="inline-flex items-center justify-center h-7 px-2 rounded-md text-xs font-medium text-forge-700 border border-forge-200 bg-forge-50 hover:bg-forge-100 whitespace-nowrap transition-colors"
        >
          Pipeline ↗
        </a>
      </div>
    </li>
  );
}

function AdminAgentsList() {
  const [agents, setAgents] = useState([]);
  const [meta, setMeta] = useState({ page: 1, per_page: PER_PAGE, total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadAgents = useCallback(async (page = 1) => {
    setLoading(true);
    setError('');

    try {
      const response = await listAgentsAdmin({ page, perPage: PER_PAGE });
      setAgents(response.data || []);
      setMeta(response.meta || { page, per_page: PER_PAGE, total: 0 });
    } catch (err) {
      setError(getApiErrorMessage(err, 'Não foi possível carregar os agentes.'));
      setAgents([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAgents(1);
  }, [loadAgents]);

  const totalPages = Math.max(1, Math.ceil(meta.total / meta.per_page));
  const hasPagination = meta.total > meta.per_page;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2">
        <div>
          <h1 className="text-2xl font-bold text-content-primary">Agentes</h1>
          <p className="text-content-muted text-sm mt-1">
            Visão global da plataforma
          </p>
        </div>
        {!loading && meta.total > 0 && (
          <p className="text-sm text-content-subtle">
            {meta.total} agente{meta.total !== 1 ? 's' : ''}
          </p>
        )}
      </div>

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <p className="text-sm text-red-700">{error}</p>
          <button
            type="button"
            onClick={() => loadAgents(meta.page)}
            className="btn-secondary text-sm px-3 py-2"
          >
            Tentar novamente
          </button>
        </div>
      )}

      {loading ? (
        <div className="surface-card px-4 py-12 text-center">
          <p className="text-content-muted text-sm">Carregando agentes...</p>
        </div>
      ) : agents.length === 0 && !error ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-white px-4 py-12 text-center">
          <p className="text-content-primary font-medium">Nenhum agente cadastrado</p>
          <p className="text-content-muted text-sm mt-2">
            Quando usuários criarem agentes, eles aparecerão aqui.
          </p>
        </div>
      ) : (
        <>
          <ul className="space-y-2">
            {agents.map((agent) => (
              <AgentRow key={agent.id} agent={agent} />
            ))}
          </ul>

          {hasPagination && (
            <div className="flex items-center justify-between gap-3 pt-2">
              <p className="text-xs text-content-subtle">
                Página {meta.page} de {totalPages}
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={meta.page <= 1 || loading}
                  onClick={() => loadAgents(meta.page - 1)}
                  className="btn-secondary text-xs px-3 py-2 disabled:opacity-40"
                >
                  Anterior
                </button>
                <button
                  type="button"
                  disabled={meta.page >= totalPages || loading}
                  onClick={() => loadAgents(meta.page + 1)}
                  className="btn-secondary text-xs px-3 py-2 disabled:opacity-40"
                >
                  Próxima
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default AdminAgentsList;
